'use client'

import { useMediaQuery } from '@morpho-org/ui-v6/hooks'
import { BREAKPOINTS_PX } from '@repo/ui/breakpoints'
import { useEffect, useState } from 'react'

/**
 * v5-compatible breakpoint names mapped onto the shared `@repo/ui` scale
 * (sm 640 · md 768 · lg 1024 · xl 1280 · 2xl 1536). The Vaults app rides the shared
 * Tailwind defaults, so the legacy `xs`/`s`/`m`/`l`/`xl` names are
 * aliases, not separate thresholds.
 */
const LEGACY_BREAKPOINT_PX = {
  xs: BREAKPOINTS_PX.sm,
  s: BREAKPOINTS_PX.md,
  m: BREAKPOINTS_PX.lg,
  l: BREAKPOINTS_PX.xl,
  xl: BREAKPOINTS_PX['2xl']
} as const

export type Breakpoint = keyof typeof LEGACY_BREAKPOINT_PX

const belowQuery = (px: number) => `(max-width: ${px - 1}px)`

/**
 * Hook that returns true when the viewport is below the `md` breakpoint (768px)
 * — the shared "mobile" cutoff (matches `@repo/ui`'s `isMobile`). Pair with
 * `md:`/`max-md:` CSS variants so JS and CSS flip at the same width. (The v5
 * predecessor cut off at 576px; VAU-1149 moved it to 768 deliberately.)
 *
 * SSR returns `false` (no `window`) — never layout-branch on this in anything
 * that paints in SSR HTML (route `loading.tsx`, Suspense fallbacks, skeletons):
 * mobile first-paints the desktop variant, then shifts after mount. Use
 * `md:`/`max-md:` classes there instead.
 */
export const useIsMobile = () => useMediaQuery(belowQuery(LEGACY_BREAKPOINT_PX.s))

/**
 * Hook that returns true when the viewport is at or above the `md` breakpoint
 * (768px) — the complement of {@link useIsMobile}, so it includes tablet widths
 * (unlike `isDesktop` in `@repo/ui`). Also `false` in SSR and during hydration,
 * which makes it the safe polarity for "`md` and up only" side effects: a mobile
 * client never sees a transient `true`.
 */
export const useIsMdUp = () => useMediaQuery(`(min-width: ${LEGACY_BREAKPOINT_PX.s}px)`)

/**
 * Hook that returns true when the viewport is below the `lg` breakpoint (1024px).
 * Pair with `lg:`/`max-lg:` CSS variants. Same SSR caveat as {@link useIsMobile}.
 */
export const useIsMedium = () => useMediaQuery(belowQuery(LEGACY_BREAKPOINT_PX.m))

/**
 * Hook that returns true when the viewport is at or above the `lg` breakpoint
 * (1024px) — the exact complement of the `lg:` CSS variant, with no fractional
 * gap (unlike `!useIsMedium()`, which misses 1023 < width < 1024). Also `false`
 * in SSR and during hydration.
 */
export const useIsLgUp = () => useMediaQuery(`(min-width: ${LEGACY_BREAKPOINT_PX.m}px)`)

/**
 * Hook that returns true when the viewport is strictly below the given
 * breakpoint, i.e. `max-width: value − 1`. Note: v5's `down(number)` used
 * `max-width: value`; the uniform −1 here matches the vaults-ui
 * `useIsBelowBreakpoint(px)` hook and Tailwind's `max-*` variants.
 */
export const useIsBelowBreakpoint = (breakpoint: Breakpoint | number) =>
  useMediaQuery(
    belowQuery(typeof breakpoint === 'number' ? breakpoint : LEGACY_BREAKPOINT_PX[breakpoint])
  )

/**
 * Detects if the device supports touch events (mobile phones and tablets).
 * Replica of ui-v5 (legacy kit) useIsTouchDevice.
 */
export const useIsTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsTouchDevice(hasTouchSupport)
  }, [])

  return isTouchDevice
}
